import React from 'react'

import { Button } from '@factorialco/deprecated-design-system'

import Callout from 'modules/core/components/Callout'
import Loading from 'modules/core/components/Loading'
import Modal from 'modules/core/components/Modal'
import { useEffect } from 'modules/core/hooks/useEffect'
import i18n from 'modules/core/lib/i18n'

import {
  ElectiveIncidenceStatus,
  inhouseStatusCopies,
} from 'modules/payroll_bundle/lib/Incidences/Status'
import { InstanceProps } from 'modules/payroll_bundle/pages/IncidencesPage/components/_StatusChangeDialog/types'

interface Props extends InstanceProps {
  newStatus?: ElectiveIncidenceStatus
}

const InhouseStatusesDialog: React.FC<Props> = ({
  newStatus,
  incidences = [],
  syncing,
  results,
  onDismiss,
  onCommit,
}) => {
  const processableIncidenceIds = new Set(
    incidences
      .filter((i) => i.status !== newStatus && i.status !== 'done' && i.status !== 'discarded')
      .map((i) => i.id)
  )

  const requiresConfirmation = newStatus === 'done'

  // if we are not setting to a final status, we commit immediately
  useEffect(() => {
    if (!requiresConfirmation) {
      onCommit(processableIncidenceIds)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const message = results?.success
    ? i18n.t('bookkeepers.incidence.bulk_status_changes.success')
    : processableIncidenceIds.size && newStatus === 'done'
    ? i18n.t('bookkeepers.incidence.bulk_status_changes.inhouse.to_done_message')
    : undefined

  const disableUI = syncing

  const success = results?.success

  // If we don't need to confirm
  if (!requiresConfirmation && syncing) {
    return null
  }

  return (
    <Modal
      illustration="pencil"
      title={i18n.t('bookkeepers.incidence.bulk_status_changes.inhouse.modal_title', {
        newStatus:
          newStatus !== 'in-preparation' && newStatus !== undefined
            ? inhouseStatusCopies()[newStatus]
            : undefined,
      })}
      description={results?.success === false ? undefined : message}
      onClose={onDismiss}
      footer={
        <>
          <Button type="secondary" onClick={onDismiss} disabled={syncing}>
            {i18n.t('common.cancel')}
          </Button>
          {success ? (
            <Button type="positive" onClick={onDismiss}>
              {i18n.t('common.done')}
            </Button>
          ) : (
            <Button
              type="negative"
              onClick={() => onCommit(processableIncidenceIds)}
              disabled={disableUI}
            >
              {i18n.t('bookkeepers.incidence.bulk_status_changes.inhouse.action')}
            </Button>
          )}
        </>
      }
    >
      {syncing && <Loading inline />}
      {results?.errorMessages && !syncing && results.success && (
        <Callout
          type="warning"
          description={i18n.t('bookkeepers.incidence.bulk_status_changes.partial_success')}
        />
      )}
      {results && !results?.success && !syncing && (
        <Callout
          type="critical"
          description={i18n.t('bookkeepers.incidence.bulk_status_changes.dialog_error')}
        />
      )}
    </Modal>
  )
}

export default InhouseStatusesDialog
